import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import {
  Button,
  CheckboxField,
  Field,
  Modal,
  PencilIcon,
  PlusIcon,
  SelectField,
  StatusBadge,
  TrashIcon,
  type Column,
} from "@fawkes/ui";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium — Handelingen: de stappen die een uitvaart doorloopt zijn hier een INSTELLING en geen
// vaste lijst in de code. Een vestiging bepaalt zelf hoe een stap heet, na hoeveel dagen hij aan de
// beurt is en waarvandaan die dagen tellen. Dat verschilt per uitvaartvorm — een collectieve
// crematie kent geen as-retour en resomeren duurt langer — dus staat er één schema per vorm.
//
// De uitvaartvorm is zelf ook instelbaar. Hij verwijst naar een Verwerkingsvorm: de technische soort
// waar de prijsberekening en de as-retourlogica aan hangen. Die soort ligt vast (enum), de naam
// eromheen niet.
//
// Dezelfde lijsten voeden straks het spoor in de dossierkop en de planningswerklijst.
//
// Endpoints RELATIEF — de dataProvider zet er één keer /api voor. De rol
// ROLE_CREMATORIUM_TARIEF_ADMIN wordt op de server afgedwongen; er is geen clientsignaal, dus de
// knoppen blijven aan en een 403 komt inline terug.

const MOMENTEN = ["AANMELDING", "ONTVANGST", "UITVAART"] as const;

// De soorten uit Verwerkingsvorm; labels via de bestaande crematoriumTarief.vorm.<ID>-sleutels.
const SOORTEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

interface VormRow {
  id: string;
  version: number;
  naam: string;
  code: string;
  verwerkingsvorm: string;
  collectief: boolean;
  asRetour: boolean;
  volgorde: number | null;
  actief: boolean;
  aantalHandelingen: number;
}

interface HandelingRow {
  id: string;
  version: number;
  uitvaartvormId: string;
  naam: string;
  vanafMoment: string | null;
  vanafHandelingId: string | null;
  dagen: number | null;
  werkdagen: boolean;
  verplicht: boolean;
  document: string | null;
  standaardUitvoerderId: string | null;
  volgorde: number | null;
  actief: boolean;
}

type SaveNotice = "conflict" | "forbidden" | "error" | null;
const statusToErr = (err: unknown): SaveNotice => {
  const code = (err as { statusCode?: number }).statusCode;
  if (code === 409) return "conflict";
  if (code === 403) return "forbidden";
  return "error";
};
const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// ── de pagina ────────────────────────────────────────────────────────────────────────────────────
export function CrematoriumHandelingOverview() {
  const t = useTranslate();

  const vormen = useCustom<VormRow[]>({ url: "crematorium/uitvaartvormen", method: "get" });
  const vormRows = vormen.data?.data ?? [];

  const [gekozen, setGekozen] = useState<string | null>(null);
  // De eerste vorm staat open zodra de lijst binnen is; daarna houdt de keuze zich vast, ook als de
  // lijst opnieuw laadt na een wijziging.
  useEffect(() => {
    if (vormRows.length === 0) return;
    if (gekozen == null || !vormRows.some((v) => v.id === gekozen)) {
      setGekozen(vormRows[0].id);
    }
  }, [vormRows, gekozen]);

  const vorm = vormRows.find((v) => v.id === gekozen) ?? null;

  const handelingen = useCustom<HandelingRow[]>({
    url: `crematorium/uitvaartvormen/${gekozen ?? ""}/handelingen`,
    method: "get",
    queryOptions: { enabled: gekozen != null },
  });
  const handelingRows = gekozen == null ? [] : (handelingen.data?.data ?? []);

  const [vormEdit, setVormEdit] = useState<VormRow | "new" | null>(null);
  const [handelingEdit, setHandelingEdit] = useState<HandelingRow | "new" | null>(null);

  const { mutate: verwijder } = useCustomMutation();
  const [notice, setNotice] = useState<SaveNotice>(null);

  const verwijderHandeling = (row: HandelingRow) => {
    setNotice(null);
    verwijder(
      {
        url: `crematorium/handelingen/${row.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          handelingen.refetch();
          vormen.refetch();
        },
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  /** "direct bij ontvangst" of "3 werkdagen na de uitvaart" — het startpunt in gewone taal. */
  const wanneer = (row: HandelingRow): string => {
    const vanaf = row.vanafHandelingId
      ? (handelingRows.find((h) => h.id === row.vanafHandelingId)?.naam ??
        t("crematoriumHandeling.vanaf.onbekend"))
      : t(`crematoriumHandeling.moment.${row.vanafMoment ?? "AANMELDING"}`);
    const dagen = row.dagen ?? 0;
    if (dagen === 0) return t("crematoriumHandeling.wanneer.direct", { args: [vanaf] });
    const soort = t(
      row.werkdagen
        ? "crematoriumHandeling.soort.werkdagen"
        : "crematoriumHandeling.soort.kalenderdagen",
    );
    return t("crematoriumHandeling.wanneer.na", { args: [dagen, soort, vanaf] });
  };

  const columns: Column<HandelingRow>[] = [
    { header: t("crematoriumHandeling.col.naam"), cell: (r) => <strong>{r.naam}</strong> },
    { header: t("crematoriumHandeling.col.wanneer"), cell: (r) => wanneer(r) },
    {
      header: t("crematoriumHandeling.col.verplicht"),
      cell: (r) =>
        r.verplicht ? (
          <StatusBadge tone="info">{t("crematoriumHandeling.verplicht.ja")}</StatusBadge>
        ) : (
          <span className="fui-muted">{t("crematoriumHandeling.verplicht.nee")}</span>
        ),
    },
    {
      header: t("crematoriumHandeling.col.document"),
      cell: (r) =>
        r.document ?? <span className="fui-muted">{t("crematoriumHandeling.document.geen")}</span>,
    },
    {
      header: t("crematoriumTarief.col.actief"),
      cell: (r) => (
        <StatusBadge tone={r.actief ? "success" : "neutral"}>
          {t(r.actief ? "crematoriumTarief.actief.ja" : "crematoriumTarief.actief.nee")}
        </StatusBadge>
      ),
    },
    {
      header: t("crematoriumTarief.col.acties"),
      align: "right",
      cell: (r) => (
        <div className="crem-rowactions">
          <button
            type="button"
            className="crem-iconbtn"
            onClick={() => setHandelingEdit(r)}
            title={t("common.edit")}
            aria-label={t("common.edit")}
          >
            <PencilIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="crem-iconbtn crem-iconbtn--danger"
            onClick={() => verwijderHandeling(r)}
            title={t("common.remove")}
            aria-label={t("common.remove")}
          >
            <TrashIcon width={16} height={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="crem-page">
      <div className="crem-page__head">
        <div className="crem-page__intro">
          <div>
            <h2 className="crem-page__title">{t("crematoriumHandeling.title")}</h2>
            <p className="crem-page__sub">{t("crematoriumHandeling.sub")}</p>
          </div>
        </div>
        <div className="crem-page__actions">
          <Button variant="secondary" onClick={() => vormen.refetch()}>
            {t("crematoriumTarief.action.refresh")}
          </Button>
          <Button onClick={() => setVormEdit("new")}>
            <PlusIcon width={16} height={16} /> {t("crematoriumHandeling.vorm.add")}
          </Button>
        </div>
      </div>

      {(vormen.isError || handelingen.isError) && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("crematoriumTarief.load.error")}
        </div>
      )}
      {notice && (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t(`crematoriumTarief.notice.${notice}`)}
        </div>
      )}

      {/* Eén lijst per uitvaartvorm; de vorm kiezen doe je met de knoppenrij erboven. */}
      <div
        className="fawkes-relatie-onderdelen"
        role="group"
        aria-label={t("crematoriumHandeling.vorm.groep")}
      >
        {vormRows.map((v) => (
          <button
            key={v.id}
            type="button"
            className={`fawkes-relatie-onderdeel${v.id === gekozen ? " is-on" : ""}`}
            aria-pressed={v.id === gekozen}
            onClick={() => setGekozen(v.id)}
          >
            {v.naam} <span className="fui-muted">{v.aantalHandelingen}</span>
          </button>
        ))}
        {vormRows.length === 0 && !vormen.isLoading && (
          <span className="fui-muted">{t("crematoriumHandeling.vorm.empty")}</span>
        )}
      </div>

      <div className="crem-card">
        <div className="crem-card__head">
          <h3 className="crem-card__title">
            {vorm
              ? t("crematoriumHandeling.stappen.van", { args: [vorm.naam] })
              : t("crematoriumHandeling.stappen.title")}
          </h3>
          <div className="crem-card__head-actions">
            <Button variant="secondary" disabled={!vorm} onClick={() => vorm && setVormEdit(vorm)}>
              <PencilIcon width={16} height={16} /> {t("crematoriumHandeling.vorm.edit")}
            </Button>
            <Button disabled={!vorm} onClick={() => setHandelingEdit("new")}>
              <PlusIcon width={16} height={16} /> {t("crematoriumHandeling.handeling.add")}
            </Button>
          </div>
        </div>
        <IndeelbareTabel<HandelingRow> tabelKey="crematorium-handeling-overview"
          columns={columns}
          rows={handelingRows}
          getRowKey={(r) => r.id}
          empty={
            handelingen.isLoading ? t("common.loading") : t("crematoriumHandeling.handeling.empty")
          }
        />
        {vorm && (
          <div className="crem-note">
            <span>
              {vorm.naam} ({vorm.code}) · {t(`crematoriumTarief.vorm.${vorm.verwerkingsvorm}`)}
              {t(
                vorm.asRetour
                  ? "crematoriumHandeling.uitleg.asRetour"
                  : "crematoriumHandeling.uitleg.geenAsRetour",
              )}
            </span>
          </div>
        )}
      </div>

      {vormEdit && (
        <VormDialog
          row={vormEdit === "new" ? null : vormEdit}
          onClose={() => setVormEdit(null)}
          onSaved={(id) => {
            setVormEdit(null);
            if (id) setGekozen(id);
            vormen.refetch();
          }}
          onDeleted={() => {
            setVormEdit(null);
            setGekozen(null);
            vormen.refetch();
          }}
        />
      )}

      {handelingEdit && vorm && (
        <HandelingDialog
          row={handelingEdit === "new" ? null : handelingEdit}
          vormId={vorm.id}
          andere={handelingRows.filter(
            (h) => h.id !== (handelingEdit === "new" ? null : handelingEdit.id),
          )}
          onClose={() => setHandelingEdit(null)}
          onSaved={() => {
            setHandelingEdit(null);
            handelingen.refetch();
            vormen.refetch();
          }}
        />
      )}
    </div>
  );
}

// ── uitvaartvorm ─────────────────────────────────────────────────────────────────────────────────
function VormDialog(props: {
  row: VormRow | null;
  onClose: () => void;
  onSaved: (id: string) => void;
  onDeleted: () => void;
}) {
  const { row, onClose, onSaved, onDeleted } = props;
  const t = useTranslate();
  const [naam, setNaam] = useState(row?.naam ?? "");
  const [code, setCode] = useState(row?.code ?? "");
  const [soort, setSoort] = useState<string>(row?.verwerkingsvorm ?? SOORTEN[0]);
  const [volgorde, setVolgorde] = useState(row?.volgorde != null ? String(row.volgorde) : "0");
  const [actief, setActief] = useState(row?.actief ?? true);

  const [naamMissing, setNaamMissing] = useState(false);
  const [codeMissing, setCodeMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    setNaamMissing(naam.trim() === "");
    setCodeMissing(code.trim() === "");
    if (naam.trim() === "" || code.trim() === "") return;
    setNotice(null);
    mutate(
      {
        url: row ? `crematorium/uitvaartvormen/${row.id}` : "crematorium/uitvaartvormen",
        method: row ? "put" : "post",
        values: {
          naam: naam.trim(),
          code: code.trim(),
          verwerkingsvorm: soort,
          volgorde: num(volgorde) ?? 0,
          actief,
          version: row?.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (data) => onSaved((data?.data as VormRow | undefined)?.id ?? row?.id ?? ""),
        onError: (err) => setNotice(statusToErr(err)),
      },
    );
  };

  // Verwijderen kan alleen zolang er geen handelingen aan hangen — de api weigert het anders, en dat
  // is de bedoeling: een lopend dossier mag niet naar verdwenen stappen wijzen.
  const verwijderVorm = () => {
    if (!row) return;
    setNotice(null);
    mutate(
      {
        url: `crematorium/uitvaartvormen/${row.id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onDeleted(), onError: (err) => setNotice(statusToErr(err)) },
    );
  };

  return (
    <Modal
      open
      title={t(row ? "crematoriumHandeling.vorm.edit.title" : "crematoriumHandeling.vorm.add.title")}
      onClose={onClose}
      footer={
        <>
          {row && (
            <Button
              variant="secondary"
              onClick={verwijderVorm}
              disabled={saving || row.aantalHandelingen > 0}
              title={
                row.aantalHandelingen > 0 ? t("crematoriumHandeling.vorm.delete.blocked") : undefined
              }
            >
              {t("common.remove")}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("crematoriumHandeling.vorm.naam")}
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
          error={naamMissing ? t("crematoriumTarief.field.naam.required") : undefined}
        />
        <Field
          label={t("crematoriumHandeling.vorm.code")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={codeMissing ? t("crematoriumHandeling.field.code.required") : undefined}
        />
        <SelectField
          label={t("crematoriumHandeling.vorm.soort")}
          value={soort}
          onChange={(e) => setSoort(e.target.value)}
          options={SOORTEN.map((s) => ({ value: s, label: t(`crematoriumTarief.vorm.${s}`) }))}
        />
        <p className="crem-veldhint">{t("crematoriumHandeling.vorm.soort.hint")}</p>
        <Field
          label={t("crematoriumTarief.klasse.col.volgorde")}
          value={volgorde}
          inputMode="numeric"
          onChange={(e) => setVolgorde(e.target.value)}
        />
        <CheckboxField
          label={t("crematoriumTarief.col.actief")}
          checked={actief}
          onChange={(e) => setActief(e.target.checked)}
        />
      </div>
    </Modal>
  );
}

// ── handeling ────────────────────────────────────────────────────────────────────────────────────
function HandelingDialog(props: {
  row: HandelingRow | null;
  vormId: string;
  andere: HandelingRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { row, vormId, andere, onClose, onSaved } = props;
  const t = useTranslate();

  const [naam, setNaam] = useState(row?.naam ?? "");
  // Het startpunt is óf een moment in het dossier óf een andere handeling. Die twee staan in één
  // keuzelijst, met een voorvoegsel in de waarde, zodat je niet eerst een soort hoeft aan te wijzen.
  const [vanaf, setVanaf] = useState(
    row?.vanafHandelingId ? `h:${row.vanafHandelingId}` : `m:${row?.vanafMoment ?? "AANMELDING"}`,
  );
  const [dagen, setDagen] = useState(row?.dagen != null ? String(row.dagen) : "0");
  const [werkdagen, setWerkdagen] = useState(row?.werkdagen ?? false);
  const [verplicht, setVerplicht] = useState(row?.verplicht ?? true);
  const [document, setDocument] = useState(row?.document ?? "");
  const [volgorde, setVolgorde] = useState(row?.volgorde != null ? String(row.volgorde) : "0");
  const [actief, setActief] = useState(row?.actief ?? true);

  const [naamMissing, setNaamMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const vanafOpties = [
    ...MOMENTEN.map((m) => ({ value: `m:${m}`, label: t(`crematoriumHandeling.moment.${m}`) })),
    ...andere.map((h) => ({
      value: `h:${h.id}`,
      label: t("crematoriumHandeling.vanaf.naHandeling", { args: [h.naam] }),
    })),
  ];

  const submit = () => {
    if (naam.trim() === "") {
      setNaamMissing(true);
      return;
    }
    setNaamMissing(false);
    setNotice(null);
    const naarHandeling = vanaf.startsWith("h:");
    mutate(
      {
        url: row
          ? `crematorium/handelingen/${row.id}`
          : `crematorium/uitvaartvormen/${vormId}/handelingen`,
        method: row ? "put" : "post",
        values: {
          naam: naam.trim(),
          vanafMoment: naarHandeling ? null : vanaf.slice(2),
          vanafHandelingId: naarHandeling ? vanaf.slice(2) : null,
          dagen: num(dagen) ?? 0,
          werkdagen,
          verplicht,
          document: document.trim() === "" ? null : document.trim(),
          volgorde: num(volgorde) ?? 0,
          actief,
          version: row?.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onSaved(), onError: (err) => setNotice(statusToErr(err)) },
    );
  };

  return (
    <Modal
      open
      title={t(
        row
          ? "crematoriumHandeling.handeling.edit.title"
          : "crematoriumHandeling.handeling.add.title",
      )}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("crematoriumHandeling.col.naam")}
          value={naam}
          onChange={(e) => setNaam(e.target.value)}
          error={naamMissing ? t("crematoriumTarief.field.naam.required") : undefined}
        />
        <SelectField
          label={t("crematoriumHandeling.vanaf.label")}
          value={vanaf}
          onChange={(e) => setVanaf(e.target.value)}
          options={vanafOpties}
        />
        <p className="crem-veldhint">{t("crematoriumHandeling.vanaf.hint")}</p>
        <Field
          label={t("crematoriumHandeling.dagen")}
          value={dagen}
          inputMode="numeric"
          onChange={(e) => setDagen(e.target.value)}
        />
        <CheckboxField
          label={t("crematoriumHandeling.werkdagen")}
          checked={werkdagen}
          onChange={(e) => setWerkdagen(e.target.checked)}
        />
        <CheckboxField
          label={t("crematoriumHandeling.col.verplicht")}
          checked={verplicht}
          onChange={(e) => setVerplicht(e.target.checked)}
        />
        <Field
          label={t("crematoriumHandeling.col.document")}
          value={document}
          onChange={(e) => setDocument(e.target.value)}
        />
        <Field
          label={t("crematoriumTarief.klasse.col.volgorde")}
          value={volgorde}
          inputMode="numeric"
          onChange={(e) => setVolgorde(e.target.value)}
        />
        <CheckboxField
          label={t("crematoriumTarief.col.actief")}
          checked={actief}
          onChange={(e) => setActief(e.target.checked)}
        />
      </div>
    </Modal>
  );
}
